import { useEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
  setLimit,
  setSkip,
  setCurrentPage,
} from '../../../store/rewardHistory/rewardHistorySlice';
import { findRewardHistory } from '../../../store/rewardHistory/api';
import Loader from '../../../components/Loader';
import Scrollbar from '../../../components/Scrollbar';
import Label from '../../../components/Label';
import { Users } from '../../../store/users/api/interfaces';
import { fDateTime } from '../../../utils/formatTime';
import { encryptString } from '../../../utils/url';

interface IProps {
  user: Users | undefined;
}

const getTypeColor = (transactionType?: string) => {
  switch (transactionType) {
    case 'earn':
    case 'birthday':
    case 'referral_referrer':
    case 'referral_referee':
      return 'success';
    case 'refund':
      return 'info';
    case 'redeem':
      return 'warning';
    case 'earn_reversal':
      return 'error';
    default:
      return 'default';
  }
};

const RewardTab: React.FC<IProps> = ({ user }) => {
  const dispatch = useAppDispatch();
  const { data, totalRecords, limit, currentPage, skip, isLoading, balance } =
    useAppSelector((state) => state.rewardHistory);

  useEffect(() => {
    if (user?.id) {
      dispatch(
        findRewardHistory({
          limit,
          skip,
          userId: user.id,
        })
      );
    }
  }, [dispatch, limit, skip, user]);

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
    dispatch(setSkip({ skip: 0 }));
    dispatch(setCurrentPage({ currentPage: 0 }));
  };

  return (
    <>
      <Stack
        direction='row'
        spacing={1}
        sx={{ px: 3, pt: 2 }}
      >
        <Typography variant='subtitle2'>Reward Points Balance:</Typography>
        <Typography variant='body2'>{balance}</Typography>
      </Stack>
      <Scrollbar>
        <TableContainer sx={{ minWidth: 1000, position: 'relative', pt: 3 }}>
          <Table size={'medium'}>
            <TableHead>
              <TableRow>
                <TableCell align='center'>{'Date'}</TableCell>
                <TableCell align='center'>{'Type'}</TableCell>
                <TableCell align='center'>{'Points'}</TableCell>
                <TableCell align='center'>{'Order'}</TableCell>
                <TableCell align='center'>{'Description'}</TableCell>
                <TableCell align='center'>{'Action'}</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading ? (
                <TableRow hover>
                  <TableCell
                    colSpan={6}
                    align='center'
                  >
                    <Loader />
                  </TableCell>
                </TableRow>
              ) : data?.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align='center'
                  >
                    {'No record found'}
                  </TableCell>
                </TableRow>
              ) : (
                data?.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell align='center'>
                      {item?.createdAt || item?.dateTime
                        ? fDateTime(item.createdAt || item.dateTime)
                        : '-'}
                    </TableCell>
                    <TableCell align='center'>
                      <Label
                        variant='ghost'
                        color={getTypeColor(item.transactionType)}
                      >
                        {item.transactionTypeLabel || item.transactionType}
                      </Label>
                    </TableCell>
                    <TableCell align='center'>
                      {Number(item.signedPoints || 0) > 0
                        ? `+${item.signedPoints}`
                        : item.signedPoints}
                    </TableCell>
                    <TableCell align='center'>
                      {item.orderReference || item.order?.orderNumber || '-'}
                    </TableCell>
                    <TableCell align='center'>
                      {item.transactionDescription || item.reason || '-'}
                    </TableCell>
                    <TableCell align='center'>
                      {item.order?.id ? (
                        <IconButton aria-label='visibility'>
                          <Link
                            to={`/order/view/${encryptString(item.order.id)}`}
                            style={{ color: '#808080' }}
                          >
                            <RemoveRedEyeIcon />
                          </Link>
                        </IconButton>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Scrollbar>

      <Box sx={{ position: 'relative' }}>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component='div'
          count={totalRecords}
          rowsPerPage={limit}
          page={Math.min(Math.round(totalRecords / limit), currentPage)}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Box>
    </>
  );
};

export default RewardTab;
